/**
 * The opening seconds of upcoming Speedies, saved while the signal is good (F-129; Tim, 2 Nov:
 * "like Facebook, the first 3–5 seconds of ~20 videos"). A3-ALIGN §7; facts in the media-delivery
 * skill, "Cloudflare Stream playback" (measured 2 Nov).
 *
 * For each Speedy, one at a time, nearest first: a pass from `media-stream`, the master playlist,
 * the 240p and audio playlists, and their init pieces and first piece (4 s; ≈ 180 KB with audio).
 * Kept in memory for the session, inside its own 5 MB (the posters and clips take the other 5 MB
 * of the Speedies' 10 MB).
 *
 * The pass request repeats `useStreamPass`'s (same endpoint, same headers) on purpose: that hook
 * is on the must-not-change list and can't be called outside a component.
 *
 * Pieces are filed by their address PATH (`<stream_uid>/video/240/seg_1.mp4`): Stream mints the
 * `p`/`s` query afresh on every playlist fetch, so the full address never repeats. Playlists are
 * filed by Speedy and kind. Every fetch is `cache: 'no-store'` (nothing goes to the browser cache).
 * An entry older than 90 minutes is dropped: its pass (≈ 2 h) would be close to expiry.
 */
import { supabase } from '@/lib/supabase';
import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';

export const STORE_LIMIT_BYTES = 5 * 1024 * 1024;
export const ENTRY_MAX_AGE_MS = 90 * 60_000;
/** Pieces saved per track: one 4-second piece (Stream's pieces are 4 s). */
export const PIECES_SAVED = 1;
const TARGET_HEIGHT = 240;

export type PlaylistKind = 'master' | 'video' | 'audio';

interface Entry {
  mediaId: string;
  masterUrl: string;
  playlists: Map<PlaylistKind, { url: string; text: string }>;
  pieceKeys: string[];
  bytes: number;
  savedAt: number;
}

const entries = new Map<string, Entry>();
const pieces = new Map<string, ArrayBuffer>();
let totalBytes = 0;

/** A piece's file key: its address path without the host and the pass. */
export function pieceKey(url: string): string {
  try {
    return new URL(url).pathname.replace(/^\/+/, '');
  } catch {
    return url.split('?')[0];
  }
}

export interface ParsedMaster {
  videoUri: string | null;
  audioUri: string | null;
}

/** The 240p rendition (else the lowest bandwidth) and the audio playlist of a master playlist. */
export function parseMaster(text: string): ParsedMaster {
  const lines = text.split('\n').map((l) => l.trim());
  let best: { uri: string; height: number; bw: number } | null = null;
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].startsWith('#EXT-X-STREAM-INF')) continue;
    const uri = lines[i + 1];
    if (!uri || uri.startsWith('#')) continue;
    const height = Number(/RESOLUTION=\d+x(\d+)/.exec(lines[i])?.[1] ?? 0);
    const bw = Number(/(?:^|,)BANDWIDTH=(\d+)/.exec(lines[i].replace('#EXT-X-STREAM-INF:', ''))?.[1] ?? Infinity);
    const better = !best
      || (height === TARGET_HEIGHT && best.height !== TARGET_HEIGHT)
      || (best.height !== TARGET_HEIGHT && bw < best.bw);
    if (better) best = { uri, height, bw };
  }
  const audio = lines.find((l) => l.startsWith('#EXT-X-MEDIA') && /TYPE=AUDIO/.test(l));
  return { videoUri: best?.uri ?? null, audioUri: audio ? /URI="([^"]+)"/.exec(audio)?.[1] ?? null : null };
}

/** The init piece and the first `count` pieces of a media playlist, as absolute addresses. */
export function parseMediaPlaylist(text: string, base: string, count = PIECES_SAVED): { init: string | null; pieces: string[] } {
  const init = /#EXT-X-MAP:URI="([^"]+)"/.exec(text)?.[1] ?? null;
  const uris = text.split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith('#'));
  return {
    init: init ? new URL(init, base).toString() : null,
    pieces: uris.slice(0, count).map((u) => new URL(u, base).toString()),
  };
}

function fresh(e: Entry | undefined): e is Entry {
  return !!e && Date.now() - e.savedAt < ENTRY_MAX_AGE_MS;
}

function drop(mediaId: string) {
  const e = entries.get(mediaId);
  if (!e) return;
  for (const k of e.pieceKeys) {
    const b = pieces.get(k);
    if (b) totalBytes -= b.byteLength;
    pieces.delete(k);
  }
  entries.delete(mediaId);
}

export const segmentStore = {
  has: (mediaId: string) => fresh(entries.get(mediaId)),
  /** The saved master address (its pass inside), for starting a Speedy before a new pass arrives. */
  masterUrl: (mediaId: string) => (fresh(entries.get(mediaId)) ? entries.get(mediaId)!.masterUrl : null),
  /** A saved playlist for this address, matched by Speedy and kind. */
  playlistFor(url: string): string | null {
    for (const e of entries.values()) {
      if (!fresh(e)) continue;
      for (const p of e.playlists.values()) if (p.url === url) return p.text;
    }
    return null;
  },
  piece: (url: string) => pieces.get(pieceKey(url)) ?? null,
  bytes: () => totalBytes,
  /** Tests only. */
  reset() {
    entries.clear();
    pieces.clear();
    totalBytes = 0;
  },
  /** Tests only. */
  _put(mediaId: string, masterUrl: string, playlists: [PlaylistKind, string, string][], saved: [string, ArrayBuffer][]) {
    const e: Entry = { mediaId, masterUrl, playlists: new Map(), pieceKeys: [], bytes: 0, savedAt: Date.now() };
    for (const [k, url, text] of playlists) e.playlists.set(k, { url, text });
    for (const [url, buf] of saved) {
      pieces.set(pieceKey(url), buf);
      e.pieceKeys.push(pieceKey(url));
      e.bytes += buf.byteLength;
      totalBytes += buf.byteLength;
    }
    entries.set(mediaId, e);
  },
};

// ── Saving ─────────────────────────────────────────────────────────────────────────────

async function fetchPass(mediaId: string, signal: AbortSignal): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return null;
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media-stream?action=token&id=${encodeURIComponent(mediaId)}`, {
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) return null;
  const pass = await res.json() as { mode?: string; url?: string };
  return pass.mode === 'stream' && pass.url ? pass.url : null;
}

async function text(url: string, signal: AbortSignal): Promise<string> {
  const r = await fetch(url, { cache: 'no-store', signal });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.text();
}
async function bytes(url: string, signal: AbortSignal): Promise<ArrayBuffer> {
  const r = await fetch(url, { cache: 'no-store', signal });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.arrayBuffer();
}

/**
 * Make room: drop expired entries, then entries not in `keep` (Speedies already passed), oldest first,
 * until `needed` bytes fit. Returns whether they fit.
 */
export function makeRoom(needed: number, keep: Set<string>): boolean {
  for (const [id, e] of [...entries]) if (!fresh(e)) drop(id);
  const others = [...entries.values()].filter((e) => !keep.has(e.mediaId)).sort((a, b) => a.savedAt - b.savedAt);
  for (const e of others) {
    if (totalBytes + needed <= STORE_LIMIT_BYTES) break;
    drop(e.mediaId);
  }
  return totalBytes + needed <= STORE_LIMIT_BYTES;
}

/** Room asked for before saving one Speedy's opening (≈ 180 KB measured, with headroom). */
const OPENING_ESTIMATE_BYTES = 256 * 1024;

/** Save one Speedy's opening. Resolves false when it isn't saved (no pass, no room, a failure). */
export async function saveOpening(mediaId: string, signal: AbortSignal, keep: Set<string> = new Set([mediaId])): Promise<boolean> {
  if (fresh(entries.get(mediaId))) return true;
  drop(mediaId);
  if (!makeRoom(OPENING_ESTIMATE_BYTES, keep)) return false;
  // Pieces saved so far for this Speedy; freed if the save doesn't finish (abort, failure, no room).
  const partial: string[] = [];
  const freePartial = () => {
    for (const k of partial) {
      const b = pieces.get(k);
      if (b) totalBytes -= b.byteLength;
      pieces.delete(k);
    }
  };
  try {
    const masterUrl = await fetchPass(mediaId, signal);
    if (!masterUrl) return false;
    const masterText = await text(masterUrl, signal);
    const { videoUri, audioUri } = parseMaster(masterText);
    if (!videoUri) return false;
    const e: Entry = { mediaId, masterUrl, playlists: new Map([['master', { url: masterUrl, text: masterText }]]), pieceKeys: [], bytes: 0, savedAt: Date.now() };
    const want: string[] = [];
    for (const [kind, uri] of [['video', videoUri], ['audio', audioUri]] as const) {
      if (!uri) continue;
      const url = new URL(uri, masterUrl).toString();
      const t = await text(url, signal);
      e.playlists.set(kind, { url, text: t });
      const parsed = parseMediaPlaylist(t, url);
      if (parsed.init) want.push(parsed.init);
      want.push(...parsed.pieces);
    }
    for (const url of want) {
      const buf = await bytes(url, signal);
      if (totalBytes + buf.byteLength > STORE_LIMIT_BYTES && !makeRoom(buf.byteLength, keep)) {
        freePartial();
        return false;
      }
      const k = pieceKey(url);
      pieces.set(k, buf);
      partial.push(k);
      e.pieceKeys.push(k);
      e.bytes += buf.byteLength;
      totalBytes += buf.byteLength;
    }
    if (signal.aborted) {
      freePartial();
      return false;
    }
    entries.set(mediaId, e);
    return true;
  } catch {
    freePartial();
    return false;
  }
}

/**
 * Save the openings of these Speedies one at a time, nearest first, until the signal aborts.
 * `keep` (the one on screen and these) is never dropped to make room; anything else may be.
 */
export async function saveOpenings(mediaIds: string[], signal: AbortSignal, keep: Set<string> = new Set(mediaIds)): Promise<void> {
  for (const id of mediaIds) {
    if (signal.aborted) return;
    if (!(await saveOpening(id, signal, keep)) && !makeRoom(OPENING_ESTIMATE_BYTES, keep)) return;
  }
}
